import React, { FC, ReactNode } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { createMarkup } from '@agria/utils/src/createMarkup';
import { useBackgroundColor } from '../../utils';
import { ThemeColorsKeys } from '../../styles/tokens/colors';
import { Typography } from '../typography/Typography';
import { Error, Info, TickCircle, Warning } from '../icon';
import { Link, LinkProps } from '../link/Link';
import { Stack } from '../stack/Stack';
import tokens from './tokens';

export interface AlertProps {
  inverse?: boolean; // Set to true when using on dark background
  link?: LinkProps;
  noIcon?: boolean;
  text?: string | ReactNode;
  variant?: 'information' | 'success' | 'error' | 'warning';
  noPadding?: boolean;
  backgroundColor?: ThemeColorsKeys;
  borderColor?: ThemeColorsKeys; // New prop for border color
}

export interface StyledAlertProps {
  background: ThemeColorsKeys;
  border: ThemeColorsKeys;
  noIcon?: boolean;
  noPadding?: boolean;
}

const StyledAlert = styled.div<StyledAlertProps>`
  ${({
    theme: { space, borderRadii, colors },
    background,
    border,
    noIcon,
    noPadding,
  }) => css`
    ${useBackgroundColor(background)};
    border-radius: ${borderRadii[tokens.borderRadius]};
    ${tokens.includeBorder &&
    !noPadding &&
    `border: 1px solid ${colors[border]};`}
    display: flex;
    flex-direction: ${noPadding ? 'row' : tokens.displayDirection};
    gap: ${space.xSmall};
    ${!noPadding && `padding: ${noIcon ? space.medium : space.small};`}

    > p:first-of-type {
      padding-top: ${space.xxxSmall};
    }
  `};
`;

const StyledIcon = styled.div`
  flex-shrink: 0;
  padding-top: 0;
`;

export const Alert: FC<AlertProps> = ({
  inverse,
  link,
  noIcon = false,
  text,
  variant = 'information',
  noPadding = false,
  backgroundColor,
  borderColor,
}) => {
  // Use backgroundColor if provided, otherwise use default logic
  let defaultBackgroundColor: ThemeColorsKeys = inverse
    ? tokens.informationInverseColor
    : 'systemInformation10';

  let defaultBorderColor: ThemeColorsKeys = inverse
    ? 'systemInformation100'
    : tokens.defaultBorderColor;

  if (variant === 'success') {
    defaultBackgroundColor = 'systemSuccess10';
    defaultBorderColor = 'systemSuccess100';
  } else if (variant === 'error') {
    defaultBackgroundColor = 'systemError10';
    defaultBorderColor = 'systemError100';
  } else if (variant === 'warning') {
    defaultBackgroundColor = 'systemError10';
    defaultBorderColor = 'systemError100';
  }

  // Use the provided colors or fall back to the defaults
  const finalBackgroundColor = backgroundColor || defaultBackgroundColor;
  const finalBorderColor = borderColor || defaultBorderColor;

  function generateText() {
    if (typeof text !== 'string') return text;

    return (
      <Typography component="p" variant="bodySmall">
        {createMarkup(text)}
      </Typography>
    );
  }

  return (
    <StyledAlert
      background={finalBackgroundColor}
      border={finalBorderColor}
      noIcon={noIcon}
      noPadding={noPadding}
    >
      {!noIcon && (
        <StyledIcon>
          {variant === 'information' && (
            <Info
              color={inverse ? 'currentColor' : 'systemInformation100'}
              size="small"
            />
          )}
          {variant === 'success' && <TickCircle color="systemSuccess100" />}
          {variant === 'warning' && <Warning color="systemError100" />}
          {variant === 'error' && <Error color="systemError100" />}
        </StyledIcon>
      )}
      <Stack gap="xxSmall">
        {generateText()}
        {link && <Link {...link} />}
      </Stack>
    </StyledAlert>
  );
};

export default Alert;
